import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { fetchByIds, fetchOrgMembers } from "@/lib/large-org";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  ArrowLeft, Bell, BellOff, Building2, Camera, Check, CheckCheck, FileText, Image as ImageIcon,
  Megaphone, Paperclip, Plus, Radio as RadioIcon, Search, Send, Users, X,
} from "lucide-react";
import { isRadioChannelKind } from "@/lib/radio";
import { canSelectRadioSite, setSelectedRadioSiteId } from "@/lib/radio/radio-site-selection";
import { RadioLivePanel } from "@/components/messages/RadioLivePanel";
import { RadioSearchResults } from "@/components/messages/RadioSearchResults";
import { toast } from "sonner";
import { formatDistanceToNow, format, isToday, isYesterday } from "date-fns";
import { clearChannelUnread, useUnreadBadges } from "@/hooks/useUnreadBadges";
import { useChannelMute } from "@/hooks/useChannelMute";
import { pushNewMessage } from "@/lib/messages.functions";
import { ModalSheet } from "@/components/ui/modal-sheet";

export const Route = createFileRoute("/_authenticated/messages")({
  component: MessagesPage,
  // `?channel=<id>` lets a new-message notification open the exact thread.
  validateSearch: (search: Record<string, unknown>): { channel?: string } =>
    typeof search.channel === "string" && search.channel
      ? { channel: search.channel }
      : {},
  head: () => ({ meta: [{ title: "Messages — MissionReady" }] }),
});

type Channel = {
  id: string;
  name: string;
  kind: string; // 'dm' | 'group' | 'site' | 'broadcast' | 'team'
  site_id: string | null;
  created_by: string;
  created_at: string;
  last_message_at: string | null;
  last_message_preview: string | null;
};
type Message = {
  id: number;
  channel_id: string;
  sender_id: string;
  body: string;
  attachments: Array<{ url: string; name: string; type: string; kind: "image" | "file" }>;
  created_at: string;
};
type Profile = { id: string; full_name: string | null; email: string | null; avatar_url: string | null };
type Site = { id: string; name: string };

const ELEVATED = ["org_owner", "admin", "supervisor", "site_supervisor", "dispatcher"];

function initials(p?: Profile | null) {
  const s = p?.full_name || p?.email || "?";
  return s.split(/\s+/).slice(0, 2).map((x) => x[0]?.toUpperCase()).join("") || "?";
}
function fmtTime(d: string) {
  const date = new Date(d);
  if (isToday(date)) return format(date, "h:mm a");
  if (isYesterday(date)) return "Yesterday";
  return format(date, "MMM d");
}
/** Exact-ish "seen" timestamp shown in the read-receipt list. */
function fmtSeenAt(d: string) {
  const date = new Date(d);
  if (isToday(date)) return format(date, "h:mm a");
  if (isYesterday(date)) return `Yesterday ${format(date, "h:mm a")}`;
  return format(date, "MMM d, h:mm a");
}

/** One member's read position in a thread + when they last read it. */
type ReadEntry = { id: number | null; at: string | null };
type UserReads = Record<string, ReadEntry>;
type ReadsMap = Record<string, { last_id: number | null; users: UserReads }>;
type ChannelListData = {
  channels: Channel[];
  memberships: Record<string, string[]>;
  profiles: Record<string, Profile>;
  reads: ReadsMap;
};

async function fetchChannelList(orgId: string, userId: string): Promise<ChannelListData> {
  const empty: ChannelListData = { channels: [], memberships: {}, profiles: {}, reads: {} };
  const { data: chs } = await supabase
    .from("message_channels")
    .select("*")
    .eq("organization_id", orgId)
    .order("last_message_at", { ascending: false, nullsFirst: false });
  const channels = (chs as Channel[]) ?? [];
  if (channels.length === 0) return empty;

  const ids = channels.map((c) => c.id);
  const [{ data: cm }, { data: rr }] = await Promise.all([
    supabase.from("channel_members").select("channel_id, user_id").in("channel_id", ids),
    supabase
      .from("channel_reads")
      .select("channel_id, user_id, last_read_message_id, last_read_at")
      .in("channel_id", ids),
  ]);

  const memberships: Record<string, string[]> = {};
  for (const r of cm ?? []) (memberships[r.channel_id] ||= []).push(r.user_id);

  const reads: ReadsMap = {};
  for (const r of rr ?? []) {
    reads[r.channel_id] ||= { last_id: null, users: {} };
    reads[r.channel_id].users[r.user_id] = {
      id: r.last_read_message_id,
      at: (r as { last_read_at?: string | null }).last_read_at ?? null,
    };
    if (r.user_id === userId) reads[r.channel_id].last_id = r.last_read_message_id;
  }

  const uids = new Set<string>();
  for (const id of ids) (memberships[id] ?? []).forEach((u) => uids.add(u));
  channels.forEach((c) => uids.add(c.created_by));
  let profiles: Record<string, Profile> = {};
  if (uids.size) {
    const ps = await fetchByIds<any>("profiles", "id, full_name, email, avatar_url", Array.from(uids));
    profiles = Object.fromEntries((ps ?? []).map((p: any) => [p.id, p]));
  }
  return { channels, memberships, profiles, reads };
}

function MessagesPage() {
  const { currentOrg, user, currentRole, isPlatformAdmin } = useAuth();
  const isElevated = !!currentRole && ELEVATED.includes(currentRole);
  const qc = useQueryClient();

  const orgId = currentOrg?.id ?? "";
  const userId = user?.id ?? "";
  const channelListKey = useMemo(() => ["messages", "list", orgId, userId] as const, [orgId, userId]);

  // Channel list, memberships, profiles + initial read receipts are owned by
  // TanStack Query so revisiting /messages is instant (cache hit) and
  // realtime can simply invalidate to trigger a background refresh.
  const { data: listData } = useQuery({
    queryKey: channelListKey,
    queryFn: () => fetchChannelList(orgId, userId),
    enabled: !!orgId && !!userId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
  const channels = listData?.channels ?? [];
  const memberships = listData?.memberships ?? {};
  const profiles = listData?.profiles ?? {};

  const [activeId, setActiveId] = useState<string | null>(null);
  // Open the thread a new-message notification pointed at, once it's loaded.
  const { channel: channelParam } = Route.useSearch();
  const openedFromLinkRef = useRef<string | null>(null);
  useEffect(() => {
    if (!channelParam || openedFromLinkRef.current === channelParam) return;
    if (!channels.some((c) => c.id === channelParam)) return;
    openedFromLinkRef.current = channelParam;
    setActiveId(channelParam);
    clearChannelUnread(channelParam);
  }, [channelParam, channels]);
  // Read receipts are mutated optimistically by ThreadView; keep them in
  // local state but seed from the query result whenever it changes.
  const [reads, setReads] = useState<ReadsMap>({});
  useEffect(() => { if (listData) setReads(listData.reads); }, [listData]);

  const [showNew, setShowNew] = useState<null | "menu" | "dm" | "group" | "site" | "broadcast">(null);

  // Existing Messages search: conversation names + Radio transcripts.
  const [listQuery, setListQuery] = useState("");
  const searching = listQuery.trim().length >= 2;
  const filteredChannels = useMemo(() => {
    const q = listQuery.trim().toLowerCase();
    if (!q) return channels;
    return channels.filter((c) => {
      const meta = describeChannel(c, memberships[c.id] ?? [], profiles, userId);
      return meta.title.toLowerCase().includes(q) || (c.name ?? "").toLowerCase().includes(q);
    });
  }, [channels, listQuery, memberships, profiles, userId]);
  const siteNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of channels) if (c.site_id) map[c.site_id] = c.name.replace(/^Radio · /, "");
    return map;
  }, [channels]);

  // Realtime: invalidate the channel-list query on any change to
  // message_channels for this org. Query will refetch + diff under the hood.
  useEffect(() => {
    if (!orgId) return;
    const ch = supabase
      .channel(`channels:${orgId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "message_channels", filter: `organization_id=eq.${orgId}` },
        () => { qc.invalidateQueries({ queryKey: ["messages", "list", orgId, userId] }); },
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [orgId, userId, qc]);

  // ThreadView can discover new sender profiles — merge them into the
  // cached query data so the conversation list reflects names immediately.
  const mergeProfiles = (p: Record<string, Profile>) => {
    qc.setQueryData<ChannelListData>(channelListKey, (old) =>
      old ? { ...old, profiles: { ...old.profiles, ...p } } : old,
    );
  };


  const activeChannel = channels.find((c) => c.id === activeId) ?? null;

  // Opening any site-scoped Messages thread selects that site for the global
  // Voice-button hold gesture. The Radio thread may not exist until the first
  // transmission resolves it, so a normal site thread must establish context
  // too. Field users continue to follow their punch/assignment automatically.
  useEffect(() => {
    if (!activeChannel?.site_id) return;
    if (!canSelectRadioSite(currentRole, isPlatformAdmin)) return;
    setSelectedRadioSiteId(orgId, activeChannel.site_id);
  }, [activeChannel?.id, activeChannel?.kind, activeChannel?.site_id, currentRole, isPlatformAdmin, orgId]);

  return (
    <div className="flex h-[calc(100dvh-4rem)] md:h-[100dvh] bg-background text-foreground">
      {/* List pane */}
      <aside className={`w-full md:w-80 md:border-r md:border-[#C0C0C0]/15 bg-background flex-col ${activeId ? "hidden md:flex" : "flex"}`}>
        <header className="flex items-center justify-between px-4 py-3 border-b border-[#C0C0C0]/15">
          <h1 className="font-display text-lg font-semibold tracking-tight">Messages</h1>
          <div className="flex items-center gap-1">
            {isElevated && (
              <button
                onClick={() => setShowNew("broadcast")}
                aria-label="New broadcast"
                className="rounded-md p-2 text-[#C0C0C0] hover:text-foreground hover:bg-[#C0C0C0]/10"
              >
                <Megaphone className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={() => setShowNew("menu")}
              aria-label="New conversation"
              className="rounded-md bg-primary p-2 text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-5 w-5" />
            </button>
          </div>
        </header>

        <div className="px-4 py-2 border-b border-[#C0C0C0]/10">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#C0C0C0]" />
            <Input
              value={listQuery}
              onChange={(e) => setListQuery(e.target.value)}
              placeholder="Search conversations & radio"
              className="pl-9 h-9"
              aria-label="Search conversations and radio transmissions"
            />
          </div>
        </div>

        {searching ? (
          <div className="flex-1 overflow-y-auto">
            <ConversationList
              channels={filteredChannels}
              activeId={activeId}
              onPick={(id) => { setActiveId(id); if (id) clearChannelUnread(id); }}
              profiles={profiles}
              memberships={memberships}
              reads={reads}
              currentUserId={user?.id ?? ""}
              unreadCounts={{}}
              mutedSet={new Set()}
            />
            <RadioSearchResults
              orgId={orgId}
              query={listQuery}
              profiles={profiles}
              siteNames={siteNames}
              onOpen={(channelId) => { setActiveId(channelId); clearChannelUnread(channelId); }}
            />
          </div>
        ) : (
          <ConversationListWithUnread
            channels={channels}
            activeId={activeId}
            onPick={(id) => { setActiveId(id); if (id) clearChannelUnread(id); }}
            profiles={profiles}
            memberships={memberships}
            reads={reads}
            currentUserId={user?.id ?? ""}
          />
        )}
      </aside>

      {/* Thread pane */}
      <section aria-label="Conversation" className={`flex-1 flex-col bg-background ${activeId ? "flex" : "hidden md:flex"}`}>
        {activeChannel ? (
          <ThreadView
            key={activeChannel.id}
            channel={activeChannel}
            orgId={orgId}
            members={memberships[activeChannel.id] ?? []}
            profiles={profiles}
            onMembersChanged={() => qc.invalidateQueries({ queryKey: channelListKey })}
            onDeleted={() => {
              setActiveId(null);
              qc.invalidateQueries({ queryKey: channelListKey });
            }}
            reads={reads[activeChannel.id]?.users ?? {}}
            currentUserId={user?.id ?? ""}
            isElevated={isElevated}
            onBack={() => setActiveId(null)}
            onMarkRead={(lastId) => {
              if (!currentOrg || !user) return;
              clearChannelUnread(activeChannel.id);
              supabase.from("channel_reads").upsert({
                channel_id: activeChannel.id,
                user_id: user.id,
                organization_id: currentOrg.id,
                last_read_message_id: lastId,
                last_read_at: new Date().toISOString(),
              }, { onConflict: "channel_id,user_id" }).then(() => {
                setReads((prev) => ({
                  ...prev,
                  [activeChannel.id]: {
                    last_id: lastId,
                    users: {
                      ...(prev[activeChannel.id]?.users ?? {}),
                      [user.id]: { id: lastId, at: new Date().toISOString() },
                    },
                  },
                }));
              });
            }}
            onProfilesUpdated={mergeProfiles}
          />
        ) : (
          <div className="m-auto text-center text-muted-foreground p-8">
            <Users className="h-10 w-10 mx-auto mb-3 text-[#C0C0C0]/60" />
            <p className="font-medium">Select a conversation</p>
            <p className="text-xs mt-1">Or tap + to start a new one</p>
          </div>
        )}
      </section>

      {/* New conversation modal */}
      {showNew && (
        <NewConversationSheet
          mode={showNew}
          setMode={setShowNew}
          orgId={currentOrg?.id ?? ""}
          userId={user?.id ?? ""}
          isElevated={isElevated}
          existingChannels={channels}
          memberships={memberships}
          onCreated={(channelId) => {
            setShowNew(null);
            setActiveId(channelId);
            qc.invalidateQueries({ queryKey: channelListKey });
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// Conversation List
// ============================================================
function ConversationListWithUnread(props: {
  channels: Channel[]; activeId: string | null; onPick: (id: string) => void;
  profiles: Record<string, Profile>; memberships: Record<string, string[]>;
  reads: ReadsMap;
  currentUserId: string;
}) {
  const { perChannel, muted } = useUnreadBadges();
  return <ConversationList {...props} unreadCounts={perChannel} mutedSet={muted} />;
}

function ConversationList({
  channels, activeId, onPick, profiles, memberships, reads, currentUserId, unreadCounts, mutedSet,
}: {
  channels: Channel[]; activeId: string | null; onPick: (id: string) => void;
  profiles: Record<string, Profile>; memberships: Record<string, string[]>;
  reads: ReadsMap;
  currentUserId: string;
  unreadCounts: Record<string, number>;
  mutedSet: Set<string>;
}) {
  if (channels.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 text-center text-muted-foreground text-sm">
        No conversations yet — tap + to start
      </div>
    );
  }
  return (
    <ul className="flex-1 overflow-y-auto divide-y divide-[#C0C0C0]/10">
      {channels.map((c) => {
        const meta = describeChannel(c, memberships[c.id] ?? [], profiles, currentUserId);
        const lastMsgAt = c.last_message_at ? fmtTime(c.last_message_at) : "";
        const count = unreadCounts[c.id] ?? 0;
        const isMuted = mutedSet.has(c.id);
        const unread = count > 0;
        return (
          <li key={c.id}>
            <button
              onClick={() => onPick(c.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                activeId === c.id ? "bg-card" : "hover:bg-card/60"
              }`}
            >
              <ChannelAvatar channel={c} meta={meta} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className={`truncate flex items-center gap-1.5 ${unread && !isMuted ? "font-semibold text-foreground" : "text-foreground/90"}`}>
                    {meta.title}
                    {isMuted && <BellOff className="h-3 w-3 text-[#C0C0C0]/70 shrink-0" />}
                  </span>
                  <span className={`text-[11px] shrink-0 ${unread && !isMuted ? "text-primary font-semibold" : "text-muted-foreground"}`}>
                    {lastMsgAt}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <span className={`truncate text-xs ${unread && !isMuted ? "text-foreground" : "text-muted-foreground"}`}>
                    {c.last_message_preview || meta.subtitle}
                  </span>
                  {unread && (
                    isMuted ? (
                      <span className="h-2 w-2 rounded-full bg-[#C0C0C0]/60 shrink-0" aria-label={`${count} unread`} />
                    ) : (
                      <span
                        className="min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold leading-none flex items-center justify-center shrink-0"
                        aria-label={`${count} unread`}
                      >
                        {count > 99 ? "99+" : count}
                      </span>
                    )
                  )}
                </div>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function describeChannel(c: Channel, members: string[], profiles: Record<string, Profile>, me: string) {
  if (c.kind === "dm") {
    const other = members.find((u) => u !== me) ?? c.created_by;
    const p = profiles[other];
    return { title: p?.full_name || p?.email || "Direct message", subtitle: "Direct message", type: "dm" as const, otherId: other };
  }
  if (c.kind === "broadcast") {
    return { title: c.name.replace(/^📢\s*Broadcast:\s*/i, "📢 ") || "📢 Broadcast", subtitle: "Broadcast", type: "broadcast" as const };
  }
  if (isRadioChannelKind(c.kind)) {
    return { title: c.name, subtitle: "Radio · site channel", type: "radio" as const };
  }
  if (c.kind === "site" || c.site_id) {
    return { title: c.name, subtitle: `Site group · ${members.length} members`, type: "site" as const };
  }
  const label = c.kind === "team" ? "Team channel" : "Group";
  return { title: c.name, subtitle: `${label} · ${members.length} members`, type: "group" as const };
}

function ChannelAvatar({ channel, meta }: { channel: Channel; meta: ReturnType<typeof describeChannel> }) {
  const base = "h-11 w-11 shrink-0 flex items-center justify-center rounded-full";
  if (meta.type === "broadcast" || channel.name?.startsWith("📢")) {
    return <div className={`${base} bg-primary/20 text-primary border border-primary/40`}><Megaphone className="h-5 w-5" /></div>;
  }
  if (meta.type === "radio") {
    return <div className={`${base} bg-primary/15 text-primary border border-primary/40`}><RadioIcon className="h-5 w-5" /></div>;
  }
  if (meta.type === "site") {
    return <div className={`${base} bg-primary/15 text-primary border border-primary/30`}><Building2 className="h-5 w-5" /></div>;
  }
  if (meta.type === "group") {
    return <div className={`${base} bg-card border border-[#C0C0C0]/30 text-[#C0C0C0]`}><Users className="h-5 w-5" /></div>;
  }
  // dm
  return (
    <Avatar className={base}>
      <AvatarFallback className="bg-card border border-[#C0C0C0]/30 text-[#C0C0C0] text-sm font-medium">
        {meta.title.split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("")}
      </AvatarFallback>
    </Avatar>
  );
}

// ============================================================
// Conversation details — members, add/remove, leave, delete
// ============================================================
function ChannelDetailsSheet({
  channel, orgId, members, profiles, currentUserId, isElevated, onClose, onChanged, onDeleted,
}: {
  channel: Channel; orgId: string; members: string[]; profiles: Record<string, Profile>;
  currentUserId: string; isElevated: boolean;
  onClose: () => void; onChanged: () => void; onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  const canManage = isElevated || channel.created_by === currentUserId;

  const { data: orgPeople = [] } = useQuery({
    queryKey: ["messages", "org-people", orgId],
    queryFn: async () => {
      const mem = await fetchOrgMembers<{ user_id: string }>(orgId, "user_id");
      const ids = mem.map((m) => m.user_id);
      if (ids.length === 0) return [] as Profile[];
      const ps = await fetchByIds<any>("profiles", "id, full_name, email, avatar_url", ids);
      return ((ps ?? []) as unknown as Profile[]);
    },
    enabled: !!orgId,
    staleTime: 60_000,
  });

  const candidates = orgPeople
    .filter((p) => p.id !== currentUserId && !members.includes(p.id))
    .filter((p) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (p.full_name ?? "").toLowerCase().includes(q) || (p.email ?? "").toLowerCase().includes(q);
    });

  const addMember = async (id: string) => {
    setBusy(true);
    const { error } = await supabase.rpc("add_channel_members", { _channel: channel.id, _user_ids: [id] });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Member added");
    onChanged();
  };

  const removeMember = async (id: string) => {
    setBusy(true);
    const { error } = await supabase.rpc("remove_channel_member", { _channel: channel.id, _user_id: id });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success(id === currentUserId ? "You left the conversation" : "Member removed");
    if (id === currentUserId) onDeleted(); else onChanged();
  };

  const deleteChannel = async () => {
    if (!window.confirm("Delete this conversation for everyone? This can't be undone.")) return;
    setBusy(true);
    const { error } = await supabase.rpc("delete_conversation", { _channel: channel.id });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Conversation deleted");
    onDeleted();
  };

  return (
    <ModalSheet
      onClose={onClose}
      labelledBy="channel-details-title"
      backdropClassName="fixed inset-0 z-[60] flex items-end md:items-center md:justify-center bg-black/70"
      className="relative w-full md:max-w-md bg-background border-t md:border border-[#C0C0C0]/20 rounded-t-2xl md:rounded-2xl flex flex-col max-h-[88dvh]"
    >
      <>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[#C0C0C0]/15">
          <h2 id="channel-details-title" className="flex-1 font-display font-semibold truncate">{channel.name}</h2>
          <button onClick={onClose} className="p-2 -mr-2 text-[#C0C0C0]" aria-label="Close conversation details"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-[#C0C0C0] mb-2">
              {members.length} {members.length === 1 ? "member" : "members"}
            </p>
            <ul className="space-y-1">
              {members.map((uid) => {
                const p = profiles[uid];
                const removable = canManage || uid === currentUserId;
                return (
                  <li key={uid} className="flex items-center gap-3 py-1.5">
                    <Avatar className="h-8 w-8">
                      {p?.avatar_url && <AvatarImage src={p.avatar_url} />}
                      <AvatarFallback className="bg-card text-[11px] text-[#C0C0C0]">{initials(p)}</AvatarFallback>
                    </Avatar>
                    <span className="flex-1 min-w-0 truncate text-sm">
                      {p?.full_name || p?.email || "Teammate"}
                      {uid === currentUserId && <span className="text-[#C0C0C0]"> (you)</span>}
                    </span>
                    {removable && (
                      <button
                        onClick={() => removeMember(uid)}
                        disabled={busy}
                        className="text-xs text-[#C0C0C0] hover:text-primary px-2 py-1 min-h-8"
                      >
                        {uid === currentUserId ? "Leave" : "Remove"}
                      </button>
                    )}
                  </li>
                );
              })}
              {members.length === 0 && (
                <li className="text-sm text-muted-foreground">No members yet — add teammates below.</li>
              )}
            </ul>
          </div>

          {canManage && (
            <div>
              {!adding ? (
                <Button variant="outline" className="w-full" onClick={() => setAdding(true)}>
                  <Plus className="h-4 w-4 mr-1" /> Add members
                </Button>
              ) : (
                <>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search teammates"
                    className="mb-2"
                  />
                  <ul className="space-y-1 max-h-56 overflow-y-auto">
                    {candidates.map((p) => (
                      <li key={p.id}>
                        <button
                          onClick={() => addMember(p.id)}
                          disabled={busy}
                          className="w-full flex items-center gap-3 py-2 text-left"
                        >
                          <Avatar className="h-8 w-8">
                            {p.avatar_url && <AvatarImage src={p.avatar_url} />}
                            <AvatarFallback className="bg-card text-[11px] text-[#C0C0C0]">{initials(p)}</AvatarFallback>
                          </Avatar>
                          <span className="flex-1 min-w-0 truncate text-sm">{p.full_name || p.email}</span>
                          <Plus className="h-4 w-4 text-primary" />
                        </button>
                      </li>
                    ))}
                    {candidates.length === 0 && (
                      <li className="text-sm text-muted-foreground py-2">Everyone is already in this conversation.</li>
                    )}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>

        {canManage && (
          <div className="px-4 py-3 border-t border-[#C0C0C0]/15 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <Button variant="destructive" className="w-full" onClick={deleteChannel} disabled={busy}>
              Delete conversation
            </Button>
          </div>
        )}
      </>
    </ModalSheet>
  );
}



// ============================================================
// Thread view
// ============================================================
function ThreadView({
  channel, orgId, members, profiles, reads, currentUserId, isElevated, onBack, onMarkRead,
  onProfilesUpdated, onMembersChanged, onDeleted,
}: {
  channel: Channel; orgId: string; members: string[]; profiles: Record<string, Profile>;
  reads: UserReads; currentUserId: string;
  isElevated: boolean; onBack: () => void; onMarkRead: (lastId: number) => void;
  onProfilesUpdated: (p: Record<string, Profile>) => void;
  onMembersChanged: () => void; onDeleted: () => void;
}) {
  const [showDetails, setShowDetails] = useState(false);
  /** Message id whose "seen by" detail list is expanded. */
  const [expandedReceipts, setExpandedReceipts] = useState<number | null>(null);
  const qc = useQueryClient();
  const messagesKey = useMemo(() => ["thread-messages", channel.id] as const, [channel.id]);

  const { data: messages = [] } = useQuery({
    queryKey: messagesKey,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("messages")
        .select("*")
        .eq("channel_id", channel.id)
        .order("created_at", { ascending: true })
        .limit(300);
      if (error) throw error;
      return ((data as unknown) as Message[]) ?? [];
    },
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  // Realtime typing indicators (broadcast-based, no DB writes)
  const [typingUsers, setTypingUsers] = useState<Record<string, { name: string; ts: number }>>({});
  const typingChanRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const lastTypingSentRef = useRef(0);
  const stopTypingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const meta = useMemo(() => describeChannel(channel, members, profiles, currentUserId), [channel, members, profiles, currentUserId]);
  const isBroadcast = meta.type === "broadcast";
  const canPost = !isBroadcast || isElevated || channel.created_by === currentUserId;

  // Fetch missing sender profiles + mark-read whenever the message list grows
  const lastSeenIdRef = useRef<number>(-1);
  useEffect(() => {
    if (!messages.length) return;
    const last = messages[messages.length - 1];
    if (last.id !== lastSeenIdRef.current) {
      lastSeenIdRef.current = last.id;
      onMarkRead(last.id);
    }
    const missing = Array.from(new Set(messages.map((m) => m.sender_id).filter((id) => !profiles[id])));
    if (missing.length) {
      (async () => {
        const ps = await fetchByIds<any>("profiles", "id, full_name, email, avatar_url", missing);
        if (ps) onProfilesUpdated(Object.fromEntries(ps.map((p: any) => [p.id, p])));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  useEffect(() => {
    const ch = supabase.channel(`thread:${channel.id}`, { config: { broadcast: { self: false } } })
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages", filter: `channel_id=eq.${channel.id}` },
        (payload) => {
          const m = payload.new as Message;
          qc.setQueryData<Message[]>(messagesKey, (prev = []) =>
            prev.some((x) => x.id === m.id) ? prev : [...prev, m]);
          if (m.sender_id !== currentUserId) onMarkRead(m.id);
          setTypingUsers((prev) => { const n = { ...prev }; delete n[m.sender_id]; return n; });
        })
      .on("broadcast", { event: "typing" }, ({ payload }) => {
        const { userId: uid, name, stop } = (payload ?? {}) as { userId?: string; name?: string; stop?: boolean };
        if (!uid || uid === currentUserId) return;
        setTypingUsers((prev) => {
          const n = { ...prev };
          if (stop) delete n[uid];
          else n[uid] = { name: name || "Someone", ts: Date.now() };
          return n;
        });
      })
      .subscribe();
    typingChanRef.current = ch;
    return () => {
      if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current);
      supabase.removeChannel(ch);
      typingChanRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel.id]);


  // Expire stale typing entries (>4s without refresh)
  useEffect(() => {
    if (Object.keys(typingUsers).length === 0) return;
    const t = setInterval(() => {
      setTypingUsers((prev) => {
        const now = Date.now();
        const n: typeof prev = {};
        for (const [k, v] of Object.entries(prev)) if (now - v.ts < 4000) n[k] = v;
        return n;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [typingUsers]);

  const broadcastTyping = (stop = false) => {
    const ch = typingChanRef.current;
    if (!ch) return;
    const me = profiles[currentUserId];
    const name = me?.full_name || me?.email || "Someone";
    if (!stop) {
      const now = Date.now();
      if (now - lastTypingSentRef.current < 1500) return;
      lastTypingSentRef.current = now;
    }
    ch.send({ type: "broadcast", event: "typing", payload: { userId: currentUserId, name, stop } });
  };

  const handleInputChange = (v: string) => {
    setInput(v);
    if (!canPost) return;
    if (v.trim().length === 0) {
      broadcastTyping(true);
      if (stopTypingTimerRef.current) { clearTimeout(stopTypingTimerRef.current); stopTypingTimerRef.current = null; }
      return;
    }
    broadcastTyping(false);
    if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current);
    stopTypingTimerRef.current = setTimeout(() => broadcastTyping(true), 3000);
  };


  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  const send = async (body: string, attachments: Message["attachments"] = []) => {
    if (!body.trim() && attachments.length === 0) return;
    if (!canPost) { toast.error("Only admins/supervisors can post here"); return; }
    setSending(true);
    const { data: inserted, error } = await supabase.from("messages").insert({
      organization_id: (channel as any).organization_id,
      channel_id: channel.id,
      sender_id: currentUserId,
      body: body.trim() || (attachments[0]?.kind === "image" ? "📷 Photo" : "📎 Attachment"),
      attachments,
    } as any).select("id").maybeSingle();
    setSending(false);
    // In-app alerts are created by a database trigger; nudge the recipients'
    // devices too. Failures here must never block sending.
    if (!error && inserted?.id) {
      pushNewMessage({ data: { messageId: Number(inserted.id) } }).catch((e) => {
        console.warn("[messages] push notify failed", e);
      });
    }
    if (error) {
      console.error("[messages] send failed", error);
      toast.error(
        /row-level security|permission/i.test(error.message)
          ? "You're not a member of this conversation yet — open its members list to join."
          : "Message couldn't be sent. Check your connection and try again.",
      );
    }
    else { setInput(""); broadcastTyping(true); if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current); }
  };

  const handleFiles = async (files: FileList | null, kind: "image" | "file") => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const uploaded: Message["attachments"] = [];
    for (const file of Array.from(files)) {
      const ext = file.name.split(".").pop() || "bin";
      const path = `${currentUserId}/${channel.id}/${Date.now()}-${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from("message-attachments").upload(path, file, { contentType: file.type });
      if (error) { toast.error(error.message); continue; }
      const { data } = await supabase.storage.from("message-attachments").createSignedUrl(path, 60 * 60 * 24 * 7);
      uploaded.push({ url: data?.signedUrl ?? "", name: file.name, type: file.type, kind });
    }
    setUploading(false);
    if (uploaded.length) await send("", uploaded);
  };

  return (
    <>
      {/* Header */}
      <header className="flex items-center gap-2 md:gap-3 px-3 md:px-4 py-3 border-b border-[#C0C0C0]/15 bg-background">
        <button onClick={onBack} className="md:hidden p-2 -ml-2 text-[#C0C0C0]" aria-label="Back">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <ChannelAvatar channel={channel} meta={meta} />
        <button
          type="button"
          onClick={() => meta.type !== "dm" && setShowDetails(true)}
          className="flex-1 min-w-0 text-left"
          aria-label={meta.type !== "dm" ? "Conversation details and members" : undefined}
        >
          <div className="font-display font-semibold truncate">{meta.title}</div>
          <div className="text-xs text-muted-foreground truncate">
            {isBroadcast ? "Broadcast · read-only for recipients" : meta.subtitle}
          </div>
        </button>
        {meta.type !== "dm" && (
          <button
            type="button"
            onClick={() => setShowDetails(true)}
            className="p-2 rounded-md text-[#C0C0C0] hover:text-foreground hover:bg-[#C0C0C0]/10"
            aria-label="Members and settings"
          >
            <Users className="h-5 w-5" />
          </button>
        )}
        <MuteToggle channelId={channel.id} />
      </header>

      {meta.type === "radio" && (
        <RadioLivePanel
          channelId={channel.id}
          siteId={(channel as any).site_id ?? null}
          siteName={meta.title}
        />
      )}

      {showDetails && (
        <ChannelDetailsSheet
          channel={channel}
          orgId={orgId}
          members={members}
          profiles={profiles}
          currentUserId={currentUserId}
          isElevated={isElevated}
          onClose={() => setShowDetails(false)}
          onChanged={onMembersChanged}
          onDeleted={() => { setShowDetails(false); onDeleted(); }}
        />
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 md:px-4 py-4 space-y-2">
        {messages.length === 0 && (
          <p className="text-center text-sm text-muted-foreground py-8">No messages yet — say hi 👋</p>
        )}
        {(() => {
          const lastMineId = [...messages].reverse().find((mm) => mm.sender_id === currentUserId)?.id ?? -1;
          return messages.map((m, i) => {
          const mine = m.sender_id === currentUserId;
          const prev = messages[i - 1];
          const showSender = !mine && (!prev || prev.sender_id !== m.sender_id);
          const sender = profiles[m.sender_id];
          const otherMembers = members.filter((u) => u !== currentUserId);
          const readers = mine ? otherMembers.filter((u) => (reads[u]?.id ?? -1) >= m.id) : [];
          const pending = mine ? otherMembers.filter((u) => (reads[u]?.id ?? -1) < m.id) : [];
          const readByAll = mine && otherMembers.length > 0 && readers.length === otherMembers.length;
          const showReceiptRow = mine && m.id === lastMineId && otherMembers.length > 0;
          const receiptsOpen = expandedReceipts === m.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                {showSender && (
                  <span className="text-[11px] text-[#C0C0C0] mb-0.5 px-2">
                    {sender?.full_name || sender?.email || "Unknown"}
                  </span>
                )}
                <div
                  className={`rounded-2xl px-3 py-2 text-sm break-words ${
                    mine
                      ? "bg-primary text-primary-foreground rounded-br-md"
                      : "bg-card text-foreground border border-[#C0C0C0]/15 rounded-bl-md"
                  }`}
                >
                  {m.attachments?.length > 0 && (
                    <div className="flex flex-col gap-2 mb-1">
                      {m.attachments.map((a, idx) =>
                        a.kind === "image" ? (
                          <button key={idx} onClick={() => setLightbox(a.url)} className="block">
                            <img src={a.url} alt={a.name} className="max-h-60 rounded-md border border-[#C0C0C0]/20" />
                          </button>
                        ) : (
                          <a key={idx} href={a.url} target="_blank" rel="noreferrer"
                            className={`flex items-center gap-2 rounded-md border px-2 py-1.5 ${
                              mine ? "border-primary-foreground/30 bg-primary-foreground/10" : "border-[#C0C0C0]/30 bg-[#0F0F0F]"
                            }`}>
                            <FileText className="h-4 w-4 shrink-0" />
                            <span className="text-xs truncate">{a.name}</span>
                          </a>
                        )
                      )}
                    </div>
                  )}
                  {m.body && <p className="whitespace-pre-wrap">{m.body}</p>}
                </div>
                <div className={`flex items-center gap-1 mt-0.5 px-2 text-[10px] text-[#C0C0C0]/80 ${mine ? "" : "self-start"}`}>
                  <span>{format(new Date(m.created_at), "h:mm a")}</span>
                  {mine && (readByAll
                    ? <CheckCheck className="h-3 w-3 text-primary" />
                    : <Check className="h-3 w-3" />)}
                </div>
                {showReceiptRow && (
                  <div className="mt-1 px-2 self-end">
                    <button
                      type="button"
                      onClick={() => setExpandedReceipts(receiptsOpen ? null : m.id)}
                      aria-expanded={receiptsOpen}
                      className="flex items-center gap-1 min-h-[28px] rounded-md hover:bg-[#C0C0C0]/10 px-1"
                    >
                      <span className="text-[10px] text-[#C0C0C0]/80">
                        {readers.length === 0
                          ? "Not seen yet"
                          : readByAll
                            ? `Seen by everyone (${readers.length})`
                            : `Seen by ${readers.length} of ${otherMembers.length}`}
                      </span>
                      {readers.length > 0 && (
                        <div className="flex -space-x-1.5">
                          {readers.slice(0, 5).map((uid) => {
                            const p = profiles[uid];
                            return (
                              <Avatar key={uid} className="h-4 w-4 border border-background">
                                {p?.avatar_url && <AvatarImage src={p.avatar_url} />}
                                <AvatarFallback className="bg-card text-[8px] text-[#C0C0C0]">
                                  {initials(p)}
                                </AvatarFallback>
                              </Avatar>
                            );
                          })}
                        </div>
                      )}
                      {readers.length > 5 && (
                        <span className="text-[10px] text-[#C0C0C0]/80">+{readers.length - 5}</span>
                      )}
                    </button>
                    {receiptsOpen && (
                      <ul className="mt-1 rounded-md border border-[#C0C0C0]/15 bg-card px-2 py-1.5 space-y-1 min-w-[11rem]">
                        {readers.map((uid) => {
                          const p = profiles[uid];
                          const at = reads[uid]?.at;
                          return (
                            <li key={uid} className="flex items-center justify-between gap-3 text-[11px]">
                              <span className="truncate text-foreground">
                                {p?.full_name || p?.email || "Unknown"}
                              </span>
                              <span className="text-[#C0C0C0]/80 shrink-0" title={at ? new Date(at).toLocaleString() : undefined}>
                                {at ? fmtSeenAt(at) : "Seen"}
                              </span>
                            </li>
                          );
                        })}
                        {pending.map((uid) => {
                          const p = profiles[uid];
                          return (
                            <li key={uid} className="flex items-center justify-between gap-3 text-[11px]">
                              <span className="truncate text-[#C0C0C0]/70">
                                {p?.full_name || p?.email || "Unknown"}
                              </span>
                              <span className="text-[#C0C0C0]/50 shrink-0">Not seen</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        });
        })()}
      </div>

      {/* Typing indicator + input bar */}
      <div className="border-t border-[#C0C0C0]/15 bg-background">
        <TypingIndicator users={Object.values(typingUsers).map((u) => u.name)} />
        <div className="p-2 md:p-3 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        {meta.type === "radio" ? (
          <p className="text-center text-xs text-muted-foreground py-2">
            Radio transcript history — hold the mic to transmit
          </p>
        ) : canPost ? (
          <form
            onSubmit={(e) => { e.preventDefault(); send(input); }}
            className="flex items-center gap-1.5"
          >
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden
              onChange={(e) => { handleFiles(e.target.files, "image"); e.target.value = ""; }} />
            <input ref={fileRef} type="file" hidden multiple
              onChange={(e) => { handleFiles(e.target.files, "file"); e.target.value = ""; }} />
            <button type="button" onClick={() => cameraRef.current?.click()} disabled={uploading}
              className="p-2 rounded-md text-[#C0C0C0] hover:text-foreground hover:bg-[#C0C0C0]/10 disabled:opacity-50"
              aria-label="Take photo">
              <Camera className="h-5 w-5" />
            </button>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="p-2 rounded-md text-[#C0C0C0] hover:text-foreground hover:bg-[#C0C0C0]/10 disabled:opacity-50"
              aria-label="Attach file">
              <Paperclip className="h-5 w-5" />
            </button>
            <Input
              value={input} onChange={(e) => handleInputChange(e.target.value)}
              onBlur={() => broadcastTyping(true)}
              placeholder={uploading ? "Uploading…" : "Message"}
              className="flex-1"
              disabled={uploading}
            />
            <Button type="submit" size="icon" disabled={(!input.trim() && !uploading) || sending}
              className="bg-primary hover:bg-primary/90">
              <Send className="h-4 w-4" />
            </Button>
          </form>
        ) : (
          <p className="text-center text-xs text-muted-foreground py-2">
            📢 Broadcast — read only
          </p>
        )}
        </div>
      </div>

      {lightbox && (
        <ModalSheet
          onClose={() => setLightbox(null)}
          label="Attached image"
          backdropClassName="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          className="relative flex items-center justify-center max-h-full max-w-full"
        >
          <>
            <img src={lightbox} alt="Attachment shared in this conversation" className="max-h-full max-w-full rounded" />
            <button
              type="button"
              onClick={() => setLightbox(null)}
              aria-label="Close image"
              className="absolute top-4 right-4 text-white p-2 min-h-11 min-w-11"
            >
              <X className="h-6 w-6" />
            </button>
          </>
        </ModalSheet>
      )}
    </>
  );
}

// ============================================================
// New conversation sheet
// ============================================================
function NewConversationSheet({
  mode, setMode, orgId, userId, isElevated, existingChannels, memberships, onCreated,
}: {
  mode: "menu" | "dm" | "group" | "site" | "broadcast";
  setMode: (m: any) => void;
  orgId: string; userId: string; isElevated: boolean;
  existingChannels: Channel[];
  memberships: Record<string, string[]>;
  onCreated: (channelId: string) => void;
}) {
  const [people, setPeople] = useState<Profile[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [groupName, setGroupName] = useState("");
  const [broadcastTitle, setBroadcastTitle] = useState("");
  const [broadcastSites, setBroadcastSites] = useState<Set<string>>(new Set());
  const [broadcastAllSites, setBroadcastAllSites] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (mode === "menu") return;
    (async () => {
      if (mode === "site" || mode === "broadcast") {
        const { data } = await supabase.from("sites").select("id, name").eq("organization_id", orgId).eq("active", true).order("name");
        setSites((data as Site[]) ?? []);
        if (mode === "broadcast") return;
        return;
      }
      const members = await fetchOrgMembers<{ user_id: string }>(orgId, "user_id");
      const ids = (members ?? []).map((m: any) => m.user_id).filter((id: string) => id !== userId);
      if (ids.length) {
        const ps = await fetchByIds<Profile>("profiles", "id, full_name, email, avatar_url", ids);
        setPeople((ps as Profile[]) ?? []);
      }
    })();
  }, [mode, orgId, userId]);

  const filtered = people.filter((p) => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return (p.full_name || "").toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q);
  });

  const close = () => setMode(null);

  // All conversation creation goes through the `create_conversation` RPC so the
  // channel and its member rows are written together (membership is what the
  // message + attachment access rules key off).
  const createConversation = async (args: {
    kind: "dm" | "group" | "team" | "broadcast";
    name: string;
    siteId?: string | null;
    memberIds?: string[];
    targetSiteIds?: string[] | null;
  }) => {
    const { data, error } = await supabase.rpc("create_conversation", {
      _org: orgId,
      _kind: args.kind,
      _name: args.name,
      _site_id: args.siteId ?? undefined,
      _member_ids: args.memberIds ?? [],
      _target_site_ids: args.targetSiteIds ?? undefined,
    });
    if (error || !data) {
      toast.error(error?.message ?? "Could not start this conversation");
      return null;
    }
    return data as unknown as string;
  };

  const createDM = async (otherId: string) => {
    setBusy(true);
    // Reuse if a DM already exists with these two members
    const existing = existingChannels.find((c) => {
      if (c.kind !== "dm") return false;
      const m = memberships[c.id] ?? [];
      return m.length === 2 && m.includes(userId) && m.includes(otherId);
    });
    if (existing) { setBusy(false); onCreated(existing.id); return; }
    const id = await createConversation({
      kind: "dm",
      name: `dm:${[userId, otherId].sort().join(":")}`,
      memberIds: [otherId],
    });
    setBusy(false);
    if (id) onCreated(id);
  };

  const createGroup = async () => {
    if (picked.size === 0 || !groupName.trim()) return;
    setBusy(true);
    const id = await createConversation({
      kind: "group",
      name: groupName.trim(),
      memberIds: Array.from(picked),
    });
    setBusy(false);
    if (id) { toast.success("Group created"); onCreated(id); }
  };

  const createSiteGroup = async (site: Site) => {
    setBusy(true);
    const id = await createConversation({ kind: "team", name: site.name, siteId: site.id });
    setBusy(false);
    if (id) onCreated(id);
  };

  const createBroadcast = async () => {
    if (!broadcastTitle.trim()) return;
    if (!isElevated) { toast.error("Only admins/supervisors can broadcast"); return; }
    const targetIds = broadcastAllSites ? null : Array.from(broadcastSites);
    if (!broadcastAllSites && (targetIds?.length ?? 0) === 0) {
      toast.error("Pick at least one site or choose All sites"); return;
    }
    setBusy(true);
    const id = await createConversation({
      kind: "broadcast",
      name: `📢 Broadcast: ${broadcastTitle.trim()}`,
      targetSiteIds: targetIds,
    });
    setBusy(false);
    if (id) { toast.success("Broadcast created"); onCreated(id); }
  };

  return (
    <ModalSheet
      onClose={close}
      labelledBy="new-conversation-title"
      backdropClassName="fixed inset-0 z-[70] bg-black/70 flex items-end md:items-center justify-center"
      className="relative w-full md:max-w-md bg-background border-t md:border rounded-t-2xl md:rounded-2xl border-[#C0C0C0]/20 h-[88dvh] md:h-auto md:max-h-[85dvh] flex flex-col"
    >
      <>
        <header className="flex items-center gap-2 px-3 md:px-4 py-3 border-b border-[#C0C0C0]/15">
          {mode !== "menu" && (
            <button onClick={() => setMode("menu")} className="p-2 -ml-2 text-[#C0C0C0]" aria-label="Back to conversation types">
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
          <h2 id="new-conversation-title" className="flex-1 font-display font-semibold truncate">
            {mode === "menu" && "New conversation"}
            {mode === "dm" && "Direct message"}
            {mode === "group" && "New group"}
            {mode === "site" && "Site group"}
            {mode === "broadcast" && "Broadcast"}
          </h2>
          <button onClick={close} className="p-2 -mr-2 text-[#C0C0C0]" aria-label="Close"><X className="h-5 w-5" /></button>
        </header>

        <div className="flex-1 overflow-y-auto overscroll-contain p-4 pb-[max(1rem,env(safe-area-inset-bottom))] space-y-3">
          {mode === "menu" && (
            <div className="grid gap-2">
              <NewMenuItem icon={<Users className="h-5 w-5" />} title="Direct message" subtitle="Chat 1:1 with a teammate" onClick={() => setMode("dm")} />
              <NewMenuItem icon={<Users className="h-5 w-5" />} title="New group" subtitle="Create a custom group chat" onClick={() => setMode("group")} />
              <NewMenuItem icon={<Building2 className="h-5 w-5" />} title="Site group" subtitle="Open a site team conversation" onClick={() => setMode("site")} />
              {isElevated && (
                <NewMenuItem icon={<Megaphone className="h-5 w-5" />} title="Broadcast" subtitle="Send to all (read-only)" onClick={() => setMode("broadcast")} />
              )}
            </div>
          )}

          {(mode === "dm" || mode === "group") && (
            <>
              {mode === "group" && (
                <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Group name" />
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#C0C0C0]" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search teammates" className="pl-9" />
              </div>
              <ul className="divide-y divide-[#C0C0C0]/10 -mx-1">
                {filtered.map((p) => {
                  const checked = picked.has(p.id);
                  return (
                    <li key={p.id}>
                      <button
                        onClick={() => {
                          if (mode === "dm") return createDM(p.id);
                          setPicked((prev) => {
                            const n = new Set(prev);
                            n.has(p.id) ? n.delete(p.id) : n.add(p.id);
                            return n;
                          });
                        }}
                        className="w-full flex items-center gap-3 px-1 py-2.5 text-left hover:bg-card/60 rounded-md"
                      >
                        <Avatar className="h-10 w-10">
                          {p.avatar_url && <AvatarImage src={p.avatar_url} />}
                          <AvatarFallback className="bg-card border border-[#C0C0C0]/30 text-[#C0C0C0]">
                            {initials(p)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-sm font-medium">{p.full_name || p.email}</div>
                          {p.full_name && <div className="truncate text-xs text-muted-foreground">{p.email}</div>}
                        </div>
                        {mode === "group" && (
                          <span className={`h-5 w-5 rounded-md border ${checked ? "bg-primary border-primary" : "border-[#C0C0C0]/40"} flex items-center justify-center`}>
                            {checked && <Check className="h-3.5 w-3.5 text-primary-foreground" />}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
                {filtered.length === 0 && (
                  <li className="text-center text-sm text-muted-foreground py-6">No teammates found</li>
                )}
              </ul>
            </>
          )}

          {mode === "site" && (
            <ul className="divide-y divide-[#C0C0C0]/10">
              {sites.map((s) => (
                <li key={s.id}>
                  <button onClick={() => createSiteGroup(s)}
                    className="w-full flex items-center gap-3 py-3 text-left hover:bg-card/60 px-1 rounded-md">
                    <div className="h-10 w-10 rounded-full bg-primary/15 border border-primary/30 text-primary flex items-center justify-center">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{s.name}</div>
                      <div className="text-xs text-muted-foreground">Site group</div>
                    </div>
                  </button>
                </li>
              ))}
              {sites.length === 0 && <li className="text-center text-sm text-muted-foreground py-6">No active sites</li>}
            </ul>
          )}

          {mode === "broadcast" && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <p className="text-[11px] text-[#C0C0C0] uppercase tracking-wider font-semibold">Broadcast title</p>
                <Input value={broadcastTitle} onChange={(e) => setBroadcastTitle(e.target.value)}
                  placeholder="e.g. Weekly briefing" />
              </div>

              <div className="space-y-2">
                <p className="text-[11px] text-[#C0C0C0] uppercase tracking-wider font-semibold">Audience</p>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button"
                    onClick={() => { setBroadcastAllSites(true); setBroadcastSites(new Set()); }}
                    className={`rounded-lg border px-3 py-2 text-sm text-left transition-colors ${
                      broadcastAllSites ? "border-primary bg-primary/15 text-foreground" : "border-[#C0C0C0]/25 text-[#C0C0C0] hover:border-[#C0C0C0]/50"
                    }`}>
                    <div className="font-medium">All sites</div>
                    <div className="text-[11px] opacity-70">Whole organization</div>
                  </button>
                  <button type="button"
                    onClick={() => setBroadcastAllSites(false)}
                    className={`rounded-lg border px-3 py-2 text-sm text-left transition-colors ${
                      !broadcastAllSites ? "border-primary bg-primary/15 text-foreground" : "border-[#C0C0C0]/25 text-[#C0C0C0] hover:border-[#C0C0C0]/50"
                    }`}>
                    <div className="font-medium">Specific sites</div>
                    <div className="text-[11px] opacity-70">{broadcastSites.size} selected</div>
                  </button>
                </div>

                {!broadcastAllSites && (
                  <ul className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-[#C0C0C0]/15 divide-y divide-[#C0C0C0]/10">
                    {sites.map((s) => {
                      const checked = broadcastSites.has(s.id);
                      return (
                        <li key={s.id}>
                          <button type="button"
                            onClick={() => setBroadcastSites((prev) => {
                              const n = new Set(prev);
                              if (n.has(s.id)) n.delete(s.id); else n.add(s.id);
                              return n;
                            })}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-card/60">
                            <span className={`h-5 w-5 rounded-md border flex items-center justify-center shrink-0 ${
                              checked ? "bg-primary border-primary" : "border-[#C0C0C0]/40"
                            }`}>
                              {checked && <Check className="h-3.5 w-3.5 text-primary-foreground" />}
                            </span>
                            <Building2 className="h-4 w-4 text-[#C0C0C0]" />
                            <span className="text-sm">{s.name}</span>
                          </button>
                        </li>
                      );
                    })}
                    {sites.length === 0 && (
                      <li className="text-center text-sm text-muted-foreground py-4">No active sites</li>
                    )}
                  </ul>
                )}
              </div>

              <div className="rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-xs text-foreground/90">
                📢 Recipients receive a read-only message. Only you and other admins/supervisors can post in this broadcast.
              </div>
            </div>
          )}
        </div>

        {(mode === "group" || mode === "broadcast") && (
          <footer className="p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-[#C0C0C0]/15">
            <Button
              className="w-full bg-primary hover:bg-primary/90"
              disabled={busy || (mode === "group"
                ? (picked.size === 0 || !groupName.trim())
                : (!broadcastTitle.trim() || (!broadcastAllSites && broadcastSites.size === 0)))}
              onClick={() => mode === "group" ? createGroup() : createBroadcast()}
            >
              {mode === "group"
                ? `Create group · ${picked.size} member${picked.size === 1 ? "" : "s"}`
                : (broadcastAllSites ? "Send broadcast · all sites" : `Send broadcast · ${broadcastSites.size} site${broadcastSites.size === 1 ? "" : "s"}`)}
            </Button>
          </footer>
        )}
      </>
    </ModalSheet>
  );
}

function NewMenuItem({ icon, title, subtitle, onClick }: { icon: React.ReactNode; title: string; subtitle: string; onClick: () => void }) {
  return (
    <button onClick={onClick}
      className="w-full flex items-center gap-3 p-3 rounded-xl border border-[#C0C0C0]/15 bg-card hover:border-primary/40 transition-colors text-left">
      <div className="h-10 w-10 rounded-full bg-primary/15 border border-primary/30 text-primary flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1">
        <div className="font-medium">{title}</div>
        <div className="text-xs text-muted-foreground">{subtitle}</div>
      </div>
    </button>
  );
}

function TypingIndicator({ users }: { users: string[] }) {
  if (users.length === 0) return null;
  const label =
    users.length === 1 ? `${users[0]} is typing`
    : users.length === 2 ? `${users[0]} and ${users[1]} are typing`
    : `${users[0]}, ${users[1]} and ${users.length - 2} more are typing`;
  return (
    <div className="px-4 pt-1.5 pb-0 flex items-center gap-2 text-[11px] text-[#C0C0C0]">
      <span className="inline-flex items-center gap-0.5" aria-hidden>
        <span className="h-1.5 w-1.5 rounded-full bg-primary/80 animate-bounce [animation-delay:-200ms]" />
        <span className="h-1.5 w-1.5 rounded-full bg-primary/80 animate-bounce [animation-delay:-100ms]" />
        <span className="h-1.5 w-1.5 rounded-full bg-primary/80 animate-bounce" />
      </span>
      <span className="truncate">{label}…</span>
    </div>
  );
}

function MuteToggle({ channelId }: { channelId: string }) {
  const { currentOrg } = useAuth();
  const { muted, toggle, loading } = useChannelMute(channelId, currentOrg?.id ?? null);
  return (
    <button
      type="button"
      onClick={() => { toggle().then(() => toast.success(muted ? "Notifications on" : "Conversation muted")); }}
      disabled={loading}
      aria-label={muted ? "Unmute conversation" : "Mute conversation"}
      title={muted ? "Unmute conversation" : "Mute conversation"}
      className={`p-2 rounded-md min-h-9 min-w-9 transition-colors ${
        muted ? "text-[#CC0000] bg-[#CC0000]/10 hover:bg-[#CC0000]/20" : "text-[#C0C0C0] hover:bg-white/5"
      }`}
    >
      {muted ? <BellOff className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
    </button>
  );
}
